import React, { useState } from 'react';
import { Trophy, TrendingUp } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { peerCountries, peerSummary } from '../../../data/peerData';
import { usePanelLink } from '../../../hooks/usePanelLink';

const getSignalStyle = (signal: string): string => {
  const styles: Record<string, string> = {
    'STRONG BUY': 'bg-data-green text-black font-bold',
    'BUY': 'bg-data-green/70 text-black font-semibold',
    'HOLD': 'bg-data-amber text-black font-semibold',
    'SELL': 'bg-data-red text-white font-semibold',
  };
  return styles[signal] || 'bg-gray-600 text-white';
};

const getRatingColor = (rating: string): string => {
  if (rating.startsWith('BBB') || rating.startsWith('A')) return 'text-data-green';
  if (rating.startsWith('BB')) return 'text-data-amber';
  if (rating.startsWith('B')) return 'text-white';
  return 'text-data-red';
};

interface PeerRankingPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const PeerRankingPanel: React.FC<PeerRankingPanelProps> = ({ isMinimized, onMinimize }) => {
  const { selectedEntity, selectEntity, isLinked } = usePanelLink('peer-ranking');

  const handleCountrySelect = (country: typeof peerCountries[0]) => {
    selectEntity({
      type: 'country',
      id: country.code,
      label: country.code,
      data: country as unknown as Record<string, unknown>,
    });
  };

  const sortedCountries = [...peerCountries].sort((a, b) => b.score - a.score);
  const maxScore = Math.max(...sortedCountries.map(c => c.score));

  return (
    <Panel id="peer-ranking" title="PEER RANKING" subtitle="EM Comparison" status="live" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full p-3">
        {/* Hero - Uzbekistan Rank */}
        <div className="text-center py-4 px-4 bg-gradient-to-b from-data-green/10 to-transparent rounded-lg border border-data-green/20 mb-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Trophy size={16} className="text-data-amber" />
            <span className="text-xs text-data-amber uppercase tracking-widest font-semibold">
              Uzbekistan Rank
            </span>
          </div>
          <div className="text-5xl font-mono font-black text-data-green">
            #{peerSummary.uzbRank}
          </div>
          <div className="text-xs text-gray-500 mt-1">of {peerSummary.totalPeers} Emerging Markets</div>
          <div className="flex items-center justify-center gap-2 mt-2">
            <TrendingUp size={14} className="text-data-green" />
            <span className="text-sm font-semibold text-data-green">{peerSummary.uzbGrowthDelta} vs avg</span>
          </div>
        </div>

        {/* Summary Stats Row */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-terminal-bg/60 rounded-lg p-2 text-center border border-terminal-border/50">
            <div className="text-lg font-mono font-bold text-data-green">{peerSummary.bestPerformer}</div>
            <div className="text-xs text-gray-500">Best Performer</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-2 text-center border border-terminal-border/50">
            <div className="text-lg font-mono font-bold text-white">{peerSummary.avgGrowth}%</div>
            <div className="text-xs text-gray-500">Avg Growth</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-2 text-center border border-terminal-border/50">
            <div className="text-lg font-mono font-bold text-data-amber">{peerSummary.riskAdjustedLeader}</div>
            <div className="text-xs text-gray-500">Risk-Adj Leader</div>
          </div>
        </div>

        {/* Country Cards */}
        <div className="flex-1 space-y-2 overflow-auto custom-scrollbar">
          {sortedCountries.slice(0, 6).map((country, idx) => {
            const isUzb = country.code === 'UZB';
            const isSelected = isLinked && selectedEntity?.id === country.code;
            return (
              <div
                key={country.code}
                onClick={() => handleCountrySelect(country)}
                className={`bg-terminal-bg/40 rounded-lg p-3 border cursor-pointer transition-all ${
                  isUzb ? 'border-data-green/50 bg-data-green/5' :
                  isSelected ? 'border-data-amber/50' : 'border-terminal-border/30 hover:border-terminal-border/60'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">#{idx + 1}</span>
                    <span className={`text-sm font-semibold ${isUzb ? 'text-data-green' : 'text-white'}`}>
                      {country.code}
                    </span>
                    <span className={`text-xs font-mono font-bold ${getRatingColor(country.creditRating)}`}>
                      {country.creditRating}
                    </span>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${getSignalStyle(country.signal)}`}>
                    {country.signal}
                  </span>
                </div>

                {/* Score Bar */}
                <div className="h-2 bg-terminal-border rounded-full overflow-hidden mb-2">
                  <div
                    className={`h-full rounded-full ${isUzb ? 'bg-data-green' : 'bg-data-blue'}`}
                    style={{ width: `${(country.score / maxScore) * 100}%` }}
                  />
                </div>

                {/* Metrics */}
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-3">
                    <span className="text-gray-500">GDP:</span>
                    <span className={`font-mono ${country.gdpGrowth >= 6 ? 'text-data-green' : 'text-white'}`}>
                      {country.gdpGrowth.toFixed(1)}%
                    </span>
                    <span className="text-gray-500">FDI:</span>
                    <span className={`font-mono ${country.fdiGrowth > 0 ? 'text-data-green' : 'text-data-red'}`}>
                      {country.fdiGrowth > 0 ? '+' : ''}{country.fdiGrowth.toFixed(0)}%
                    </span>
                  </div>
                  <span className="text-lg font-mono font-black text-white">{country.score}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.NATEX} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default PeerRankingPanel;
